(function () {
  'use strict';

  /*
    ARTMERE curated collection measurement.

    One shared file for every curated collection page. Per-page values are read from
    data attributes on the section root, so no page needs its own tracking file.

    It reuses the existing ARTMERE measurement contract only:
      view_item_list  once per loaded edit
      select_item     once per product click
      artmere_collection_to_pdp  via the existing PDP bridge sessionStorage key

    No new event names, containers or transports are introduced.
  */

  var ROOT_SELECTOR = '[data-artmere-curated]';
  var BRIDGE_STORAGE_KEY = 'artmere_search_v2_selection_v1';
  var TRANSPORT_MAX_WAIT_MS = 15000;
  var TRANSPORT_SETTLE_MS = 400;
  var INIT_FLAG = '__artmereCuratedCollectionInitialized';

  function bounded(value, limit) {
    return String(value == null ? '' : value).slice(0, limit || 120);
  }

  function eventId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    return 'artmere-curated-' + Date.now() + '-' + Math.random().toString(16).slice(2);
  }

  function publish(name, extra, discriminator) {
    if (typeof window.artmerePublishMeasurementEvent !== 'function') return false;
    return window.artmerePublishMeasurementEvent(name, extra || {}, discriminator || eventId());
  }

  function whenMeasurementTransportReady(callback) {
    var fired = false;
    var started = Date.now();

    function run() {
      if (fired) return;
      fired = true;
      callback();
    }

    function attempt() {
      if (fired) return;
      if (typeof window.gtag === 'function' && typeof window.artmerePublishMeasurementEvent === 'function') {
        setTimeout(run, TRANSPORT_SETTLE_MS);
        return;
      }
      if (Date.now() - started >= TRANSPORT_MAX_WAIT_MS) {
        run();
        return;
      }
      setTimeout(attempt, 200);
    }

    if (document.readyState === 'complete') {
      attempt();
    } else {
      window.addEventListener('load', attempt, { once: true });
      setTimeout(attempt, TRANSPORT_MAX_WAIT_MS);
    }
  }

  function itemFromLink(link, listId, listName, fallbackIndex) {
    if (!link) return null;
    var href = link.getAttribute('href') || '';
    var item = {
      item_id: bounded(link.getAttribute('data-item-id'), 80),
      item_variant: bounded(link.getAttribute('data-item-variant-id'), 80),
      item_name: bounded(link.getAttribute('data-item-name'), 160),
      item_list_id: listId,
      item_list_name: listName,
      index: Number(link.getAttribute('data-item-index') || fallbackIndex || 1),
      price: Number(link.getAttribute('data-item-price') || 0),
      currency: bounded(link.getAttribute('data-item-currency') || 'USD', 12),
      destination_path: bounded(href, 240)
    };
    if (!item.item_id || !item.item_name || !href) return null;
    return item;
  }

  function storeSelection(selection) {
    try {
      sessionStorage.setItem(BRIDGE_STORAGE_KEY, JSON.stringify(selection));
    } catch (error) {}
  }

  function setMeta(name, content) {
    if (!content) return;
    var tag = document.querySelector('meta[name="' + name + '"]');
    if (!tag) {
      tag = document.createElement('meta');
      tag.setAttribute('name', name);
      document.head.appendChild(tag);
    }
    tag.setAttribute('content', content);
  }

  function setPropertyMeta(property, content) {
    if (!content) return;
    var tag = document.querySelector('meta[property="' + property + '"]');
    if (!tag) {
      tag = document.createElement('meta');
      tag.setAttribute('property', property);
      document.head.appendChild(tag);
    }
    tag.setAttribute('content', content);
  }

  function setCanonical(path) {
    if (!path) return;
    var canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', new URL(path, window.location.origin).href);
  }

  function applyPageMetadata(root) {
    var title = root.getAttribute('data-meta-title');
    if (title) document.title = title;
    setMeta('description', root.getAttribute('data-meta-description'));
    setCanonical(root.getAttribute('data-canonical-path'));

    var socialTitle = root.getAttribute('data-social-title');
    var socialDescription = root.getAttribute('data-social-description');
    var socialImage = root.getAttribute('data-social-image');
    var canonicalPath = root.getAttribute('data-canonical-path');
    setPropertyMeta('og:title', socialTitle);
    setPropertyMeta('og:description', socialDescription);
    setPropertyMeta('og:type', 'website');
    setPropertyMeta('og:url', canonicalPath ? new URL(canonicalPath, window.location.origin).href : '');
    setPropertyMeta('og:image', socialImage);
    setMeta('twitter:title', socialTitle);
    setMeta('twitter:description', socialDescription);
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:image', socialImage);
  }

  function init() {
    var root = document.querySelector(ROOT_SELECTOR);
    if (!root || window[INIT_FLAG]) return;
    window[INIT_FLAG] = true;

    var listId = bounded(root.getAttribute('data-item-list-id') || 'artmere_curated_collection', 80);
    var listName = bounded(root.getAttribute('data-item-list-name') || 'ARTMERE Curated Collection', 160);

    applyPageMetadata(root);
    root.setAttribute('data-artmere-curated-tracking', 'ready');

    /*
      Only the primary grid feeds view_item_list. Thematic sub-sections re-use
      works that are already in the grid, so counting them again would inflate
      the list and break the exact-once contract.
    */
    var gridLinks = Array.prototype.slice.call(root.querySelectorAll('[data-curated-grid-item]'));
    var items = gridLinks
      .map(function (link, index) {
        return itemFromLink(link, listId, listName, index + 1);
      })
      .filter(Boolean);

    if (items.length) {
      whenMeasurementTransportReady(function () {
        publish(
          'view_item_list',
          {
            surface: listId,
            item_list_id: listId,
            item_list_name: listName,
            item_count: items.length,
            ecommerce: {
              item_list_id: listId,
              item_list_name: listName,
              items: items
            }
          },
          eventId()
        );
      });
    }

    root.addEventListener(
      'click',
      function (event) {
        if (event.__artmereCuratedHandled) return;
        var link =
          event.target && event.target.closest ? event.target.closest('[data-curated-product-click]') : null;
        if (!link || !root.contains(link)) return;
        event.__artmereCuratedHandled = true;

        var item = itemFromLink(link, listId, listName, 1);
        if (!item) return;
        var selectedAt = new Date().toISOString();
        storeSelection({ selected_at: selectedAt, item: item });
        publish(
          'select_item',
          {
            surface: listId,
            item_list_id: item.item_list_id,
            item_list_name: item.item_list_name,
            selected_item_id: item.item_id,
            selected_item_name: item.item_name,
            selected_item_index: item.index,
            destination_path: item.destination_path,
            ecommerce: {
              item_list_id: item.item_list_id,
              item_list_name: item.item_list_name,
              items: [item]
            }
          },
          selectedAt + ':' + item.item_id
        );
      },
      true
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
